import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import TraderStatsChart from '../components/TraderStatsChart';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Target, Shield, TrendingUp, ChevronRight,
  Copy, Check, Share2, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { openTelegramCta, getTelegramDeepLink } from '../lib/telegram';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResultsPage = () => {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const response = await axios.get(`${API}/quiz/results/${resultId}`);
        setResult(response.data);
      } catch (err) {
        setError('Result not found');
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [resultId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-tc-bg flex items-center justify-center" data-testid="results-loading">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-tc-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-tc-text-secondary">Sedang menyiapkan hasil Anda...</p>
        </div>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="min-h-screen bg-tc-bg flex items-center justify-center" data-testid="results-error">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-semibold text-white mb-4">Hasil tidak ditemukan</h2>
          <p className="text-tc-text-secondary mb-8">Hasil yang Anda cari tidak tersedia.</p>
          <Button onClick={() => navigate('/')} className="bg-tc-primary hover:bg-tc-primary-hover">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke Beranda
          </Button>
        </div>
      </div>
    );
  }

  const { trader_info, risk_profile, scores } = result;

  const typeColors = {
    scalper: '#FF6B6B',
    intraday: '#4ECDC4',
    swing: '#3A6EA5',
    position: '#64FFDA'
  };

  return (
    <div className="relative min-h-screen bg-tc-bg" data-testid="results-page">
      {/* Seamless Background Image */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none opacity-20 mix-blend-screen"
        style={{
          backgroundImage: `url('/images/backgrounds/results_page_bg_1774768191946.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      />

      {/* Header overlay */}
      <div className="relative z-10 w-full">
      <header className="fixed top-0 left-0 right-0 z-50 bg-tc-bg/80 backdrop-blur-md border-b border-tc-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-tc-text-secondary hover:text-white transition-colors"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Kembali</span>
          </button>
        </div>
      </header>

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Result Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-tc-primary font-mono text-sm tracking-wider uppercase mb-4">
              Hasil pencocokan Anda
            </p>
            <h1 className="text-5xl md:text-7xl font-heading font-extrabold text-white mb-6">
              {trader_info.name}
            </h1>
            {/* Share buttons */}
            <div className="flex justify-center gap-3 mb-6">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  toast.success('Link hasil disalin');
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-tc-surface border border-tc-border text-tc-text-secondary hover:text-white hover:border-tc-primary/50 transition-colors text-sm"
                data-testid="share-copy-btn"
              >
                <Copy className="w-4 h-4" />
                Salin Link
              </button>
              <button
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Hasil quiz trading saya: ${trader_info.name} - ${window.location.href}`)}`, '_blank', 'noopener,noreferrer')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-tc-surface border border-tc-border text-tc-text-secondary hover:text-white hover:border-tc-primary/50 transition-colors text-sm"
                data-testid="share-wa-btn"
              >
                <Share2 className="w-4 h-4" />
                WhatsApp
              </button>
              <button
                onClick={() => window.open(`https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(`Hasil quiz trading saya: ${trader_info.name}`)}`, '_blank', 'noopener,noreferrer')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-tc-surface border border-tc-border text-tc-text-secondary hover:text-white hover:border-tc-primary/50 transition-colors text-sm"
                data-testid="share-tg-btn"
              >
                <Send className="w-4 h-4" />
                Telegram
              </button>
            </div>
            <p className="text-xl text-tc-text-secondary max-w-2xl mx-auto leading-relaxed">
              {trader_info.description}
            </p>
          </motion.div>

          {/* Stats Grid */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid md:grid-cols-2 gap-8 mb-16"
          >
            {/* Stats Chart */}
            <div className="glass-card rounded-2xl p-8">
              <h3 className="text-lg font-heading font-semibold text-white mb-6 flex items-center gap-2">
                <Target className="w-5 h-5 text-tc-primary" />
                Profil trading Anda
              </h3>
              <TraderStatsChart
                riskProfile={risk_profile}
                accentColor={typeColors[result.trader_type]}
                traderType={result.trader_type}
              />
            </div>

            {/* Score Breakdown */}
            <div className="glass-card rounded-2xl p-8">
              <h3 className="text-lg font-heading font-semibold text-white mb-6 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-tc-primary" />
                Skor kecocokan gaya
              </h3>
              <div className="space-y-5">
                {Object.entries(scores).map(([type, score]) => {
                  const maxScore = Math.max(...Object.values(scores));
                  const percentage = ((score + 20) / (maxScore + 40)) * 100;
                  const isMatch = type === result.trader_type;
                  return (
                    <div key={type}>
                      <div className="flex justify-between items-center mb-2">
                        <span className={`font-medium capitalize ${isMatch ? 'text-white' : 'text-tc-text-secondary'}`}>
                          {type === 'intraday' ? 'Intraday' : type.charAt(0).toUpperCase() + type.slice(1)}
                          {isMatch && <span className="ml-2 text-xs text-tc-secondary">(Paling Cocok)</span>}
                        </span>
                        <span className="text-sm text-tc-text-secondary">{score} pts</span>
                      </div>
                      <div className="h-2 bg-tc-surface-highlight rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.max(percentage, 5)}%` }}
                          transition={{ duration: 0.8, delay: 0.3 }}
                          className="h-full rounded-full"
                          style={{ backgroundColor: isMatch ? typeColors[type] : '#3A6EA5' }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Characteristics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="glass-card rounded-2xl p-8 mb-16 relative overflow-hidden"
          >
            <h3 className="text-lg font-heading font-semibold text-white mb-6">
              Karakteristik Anda
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {trader_info.characteristics.map((char, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-tc-primary/20 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <ChevronRight className="w-4 h-4 text-tc-primary" />
                  </div>
                  <span className="text-tc-text-secondary">{char}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Chart Preview — Real TradingView Output */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mb-16"
          >
            {(() => {
              const chartMap = {
                scalper: { image: '/images/social-proof/v2-chart-5m.png', tf: '5 Minit', label: 'Scalper' },
                intraday: { image: '/images/social-proof/v2-chart-15m.png', tf: '15 Minit', label: 'Intraday' },
                swing: { image: '/images/social-proof/v2-chart-4h.png', tf: '4 Jam', label: 'Swing' },
                position: { image: '/images/social-proof/v2-chart-4h.png', tf: '4 Jam', label: 'Swing' },
              };
              const chart = chartMap[result.trader_type] || chartMap.swing;
              return (
                <div className="glass-card rounded-2xl p-8 border border-tc-border relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-tc-primary/5 to-transparent" />
                  <div className="relative">
                    <h3 className="text-lg font-heading font-semibold text-white mb-2 flex items-center gap-2">
                      <Target className="w-5 h-5 text-tc-primary" />
                      Chart XAUUSD — Timeframe {chart.label}
                    </h3>
                    <p className="text-tc-text-secondary text-sm mb-6">
                      Contoh setup sebenar dari bot AI kami pada timeframe {chart.tf}. Setiap analisis direka dengan TP lebih besar daripada SL — risk:reward minimum 1:2.
                    </p>
                    <img
                      src={chart.image}
                      alt={`XAUUSD ${chart.tf} chart analysis`}
                      className="w-full rounded-xl border border-tc-border"
                      loading="lazy"
                    />
                    <p className="text-xs text-tc-text-secondary mt-3 text-center">
                      Screenshot sebenar dari TradingView. Bukan mockup.
                    </p>
                  </div>
                </div>
              );
            })()}
          </motion.div>

          {/* Strategy Examples + 73% Confidence Hook */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mb-16 relative"
          >
            <div className="mb-8">
              <h3 className="text-2xl font-heading font-semibold text-white mb-2">
                Strategi yang cocok untuk Anda
              </h3>
              <p className="text-tc-text-secondary">
                Berikut contoh strategi yang sesuai dengan gaya trading Anda. Pelajari rules-nya, pahami logikanya.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {(() => {
                const STRATEGY_EMBEDS = {
                  scalper: [
                    { name: 'Bollinger Band Reversal Scalping', type: 'Scalper', embedId: '6338917399265280' },
                    { name: 'EMA 9/20 Pullback Scalping', type: 'Scalper', embedId: '4709342986633216' },
                  ],
                  intraday: [
                    { name: 'BRST (H1) Intraday', type: 'Intraday', embedId: '5788803559587840' },
                    { name: 'Smart Money Concept', type: 'Intraday', embedId: '5680752550739968' },
                  ],
                  swing: [
                    { name: 'Trend Pullback Strategy', type: 'Swing', embedId: '5198646196305920' },
                    { name: 'Swing (SRBS)', type: 'Swing', embedId: '6404530071142400' },
                    { name: 'Breakout Retest Strategy', type: 'Swing', embedId: '5446416752705536' },
                  ],
                  position: [
                    { name: 'Trend Pullback Strategy', type: 'Swing', embedId: '5198646196305920' },
                    { name: 'Swing (SRBS)', type: 'Swing', embedId: '6404530071142400' },
                    { name: 'Breakout Retest Strategy', type: 'Swing', embedId: '5446416752705536' },
                  ],
                };

                const strategies = STRATEGY_EMBEDS[result.trader_type] || STRATEGY_EMBEDS.swing;

                return strategies.map((strat, idx) => (
                  <motion.div
                    key={strat.embedId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.6 + idx * 0.1 }}
                    className="glass-card rounded-xl overflow-hidden"
                    data-testid={`strategy-embed-${strat.embedId}`}
                  >
                    {/* Strategy header */}
                    <div className="p-4 flex items-center justify-between">
                      <div>
                        <h4 className="text-lg font-heading font-semibold text-white">
                          {strat.name}
                        </h4>
                        <span className="text-xs text-tc-primary font-mono">{strat.type}</span>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-tc-primary/10 flex items-center justify-center">
                        <span className="text-tc-primary font-bold text-sm">{idx + 1}</span>
                      </div>
                    </div>
                    {/* Embed */}
                    <div className="w-full aspect-[4/3] bg-tc-surface-highlight">
                      <iframe
                        src={`https://www.appdemostore.com/embed?id=${strat.embedId}`}
                        title={strat.name}
                        className="w-full h-full border-0"
                        loading="lazy"
                        allow="fullscreen"
                      />
                    </div>
                  </motion.div>
                ));
              })()}
            </div>

            {/* 73% Confidence Hook - Telegram CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="mt-10 glass-card rounded-2xl p-8 text-center border border-tc-primary/30 relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-tc-primary/5 to-transparent" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-tc-secondary/10 border border-tc-secondary/20 mb-6">
                  <TrendingUp className="w-4 h-4 text-tc-secondary" />
                  <span className="text-sm text-tc-secondary font-mono font-medium">73% Confidence Level</span>
                </div>
                <h3 className="text-2xl font-heading font-semibold text-white mb-4">
                  Mau pakai strategi ini dengan bot AI?
                </h3>
                <p className="text-tc-text-secondary mb-6 max-w-xl mx-auto">
                  Strategi di atas sudah diuji dengan tingkat akurasi 73%. Di komunitas Telegram kami, Anda bisa langsung gunakan strategi ini lewat bot AI yang memberikan setup real-time untuk XAUUSD.
                </p>
                <Button
                  onClick={() => window.open(getTelegramDeepLink(resultId), '_blank', 'noopener,noreferrer')}
                  className="bg-tc-primary hover:bg-tc-primary-hover text-white px-10 py-6 text-lg font-bold tracking-wide rounded-full btn-glow"
                  data-testid="strategy-telegram-cta"
                >
                  <Send className="w-5 h-5 mr-3" />
                  GABUNG TELEGRAM SEKARANG
                </Button>
                <p className="text-xs text-tc-text-secondary mt-4">
                  Gratis. Langsung akses bot AI + komunitas trader.
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* SOP Preview */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="glass-card rounded-2xl p-8 mb-16"
          >
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-6 h-6 text-tc-primary" />
              <h3 className="text-lg font-heading font-semibold text-white">
                Cuplikan SOP
              </h3>
            </div>
            <div className="bg-tc-surface-highlight/50 rounded-xl p-6 border border-tc-border">
              <p className="text-tc-text-secondary font-mono text-sm leading-relaxed">
                {trader_info.sop_preview}
              </p>
            </div>
            <p className="text-sm text-tc-text-secondary mt-4">
              Gunakan cuplikan ini sebagai materi edukasi. Selalu kelola risiko secara mandiri.
            </p>
          </motion.div>

          {/* Onboard Code + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="text-center relative overflow-hidden"
          >
            <h3 className="text-2xl font-heading font-semibold text-white mb-4">
              Lanjut ke grup Telegram Anda
            </h3>
            <p className="text-tc-text-secondary mb-8 max-w-lg mx-auto">
              Masuk ke Telegram, lalu ketik perintah di bawah untuk menghubungkan profil Anda dengan bot AI. Bot akan merekomendasikan perintah analisis chart yang sesuai (/scalper, /intraday, atau /swing).
            </p>

            {/* Onboard code box */}
            <div className="glass-card rounded-2xl p-6 mb-8 max-w-md mx-auto border border-tc-primary/30" data-testid="onboard-code-box">
              <p className="text-sm text-tc-text-secondary mb-3">Ketik ini di bot <span className="text-white font-medium">@TCASIWBot</span>:</p>
              <div className="flex items-center justify-center gap-3 bg-tc-surface-highlight/50 rounded-xl px-5 py-4 border border-tc-border">
                <code className="text-lg font-mono text-tc-primary font-bold select-all" data-testid="onboard-code">
                  /onboard {resultId}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`/onboard ${resultId}`);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="p-2 rounded-lg hover:bg-tc-surface-highlight transition-colors"
                  data-testid="copy-onboard-code-btn"
                >
                  {copied
                    ? <Check className="w-5 h-5 text-tc-secondary" />
                    : <Copy className="w-5 h-5 text-tc-text-secondary hover:text-white" />
                  }
                </button>
              </div>
              <p className="text-xs text-tc-text-secondary mt-3">
                Bot AI akan mengenali tipe trader Anda dan merekomendasikan perintah analisis chart yang sesuai.
                {result.trader_type === 'position' && ' Sebagai position trader, Anda akan diarahkan ke /swing sebagai strategi bot terdekat.'}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => window.open(getTelegramDeepLink(resultId), '_blank', 'noopener,noreferrer')}
                className="bg-tc-primary hover:bg-tc-primary-hover text-white px-8 py-6 text-lg font-bold tracking-wide rounded-full btn-glow"
                data-testid="join-campaign-btn"
              >
                MASUK TELEGRAM
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="border-tc-border text-tc-text-secondary hover:text-white px-8 py-6 text-lg rounded-full"
                data-testid="retake-quiz-btn"
              >
                KEMBALI KE BERANDA
              </Button>
            </div>
            <p className="text-sm text-tc-text-secondary mt-4">
              Trading mengandung risiko dan tidak ada jaminan profit.
            </p>
          </motion.div>
        </div>
      </main>
      </div>
    </div>
  );
};

export default ResultsPage;
